import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useOfferBuilderContext } from '../OfferBuilderContext';

const getNextMarketClearTime = (
  selected: ReturnType<typeof useOfferBuilderContext>['state']['selected']
): string | null => {
  const clearTimes = selected
    .map((market) => market.marketNextClearTime)
    .filter((time): time is string => time !== null)
    .map((time) => new Date(time))
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  return clearTimes.length > 0 ? clearTimes[0].toISOString() : null;
};

const NextSteps = () => {
  const { t } = useTranslation();
  const {
    state: { selected, builderVariant },
  } = useOfferBuilderContext();
  const { asDateStringText, asNumber } = useLocaleFormat();
  const nextClearTime = getNextMarketClearTime(selected);
  const isAcquisition = builderVariant === 'acquisition';

  const formatClearTime = (dateString: string | null): { time: string; timezone: string; date: string } => {
    if (!dateString) {
      return { time: 'N/A', timezone: '', date: 'N/A' };
    }

    try {
      const dateObj = new Date(dateString);

      // validate date before formatting
      if (isNaN(dateObj.getTime())) {
        return { time: 'N/A', timezone: '', date: 'N/A' };
      }

      const time = format(dateObj, 'h:mm a');
      const timezoneParts = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
      }).formatToParts(dateObj);
      const timezone = timezoneParts.find((part) => part.type === 'timeZoneName')?.value || '';
      const date = asDateStringText({ date: dateObj, isUTC: false });

      return { time, timezone, date };
    } catch {
      return { time: 'N/A', timezone: '', date: 'N/A' };
    }
  };

  const { time, timezone, date } = formatClearTime(nextClearTime);

  if (isAcquisition) {
    return (
      <div>
        <div className="mb-2 text-lg font-medium">{t('offerConfirmation.nextSteps')}</div>
        <div className="space-y-6 rounded-lg border-[0.5px] border-stroke bg-white p-6 shadow-sm">
          <div className="flex gap-4">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-lg font-medium text-white">
              {asNumber({ value: 1 })}
            </div>
            <div>
              <div className="font-medium">{t('offerConfirmation.acquisition.step1Title')}</div>
              <div className="mt-1 text-sm">{t('offerConfirmation.acquisition.step1Description')}</div>
              <div className="mt-2 rounded-lg border border-stroke bg-canvas p-2 text-center">
                <div className="text-sm font-bold">
                  {time} {timezone}
                </div>
                <div className="text-sm font-bold">{date}</div>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-lg font-medium text-white">
              {asNumber({ value: 2 })}
            </div>
            <div>
              <div className="font-medium">{t('offerConfirmation.acquisition.step2Title')}</div>
              <div className="mt-1 text-sm">{t('offerConfirmation.acquisition.step2Description')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 text-lg font-medium">{t('offerConfirmation.nextSteps')}</div>
      <div className="space-y-6 rounded-lg border-[0.5px] border-stroke bg-white p-6 shadow-sm">
        <div className="flex gap-4">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-lg font-medium text-white">
            {asNumber({ value: 1 })}
          </div>
          <div>
            <div className="font-medium">{t('offerConfirmation.step1Title')}</div>
            <div className="mt-1 text-sm">{t('offerConfirmation.step1Description')}</div>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-lg font-medium text-white">
            {asNumber({ value: 2 })}
          </div>
          <div>
            <div className="font-medium">{t('offerConfirmation.step2Title')}</div>
            <div className="mt-1 text-sm">{t('offerConfirmation.step2Description')}</div>
            <div className="mt-2 rounded-lg border border-stroke bg-canvas p-2 text-center">
              <div className="text-xs">{t('offerConfirmation.makeChangesBefore')}</div>
              <div className="text-sm font-bold">
                {time} {timezone}
              </div>
              <div className="text-sm font-bold">{date}</div>
            </div>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-lg font-medium text-white">
            {asNumber({ value: 3 })}
          </div>
          <div>
            <div className="font-medium">{t('offerConfirmation.step3Title')}</div>
            <div className="mt-1 text-sm">{t('offerConfirmation.step3Description')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NextSteps;
